/**
 * The inspector (2026-10-28, Adam: "a liquid glass bubble that always lives to the side"): what can be
 * changed about what is selected on the plan, in a pane of glass at the screen's right — always the
 * right, and always halfway down it — beside the plan rather than over it. Nothing is behind a
 * backdrop: the plan pans, zooms and is worked on while it is open, and what is changed shows on the
 * plan as it is changed.
 *
 * Closed, it is a pill of glass standing on its end at the right, halfway down. Tapped, the pill grows
 * into the pane — every setting of what is selected (or that nothing is) — and Done folds it back
 * into the pill: one shape, the glass clipped from the pill's outline to the pane's and back, what it
 * holds coming in as it opens. A button on the selection's bar (Name, Note, Edit text …) opens it on
 * just that, with a way on to everything (`SidePage.more`). A row may lead on to a page of its own
 * (all the words ready to use, the map's test labels): it slides in, a back button at its head, and
 * slides back out — the inspector never closes to go back.
 *
 * It is one element for the screen's life (its drawn glass has one canvas of its own, put in once:
 * ui/glass.ts), refilled for each use; the pill likewise.
 */
import { clear, h, haptic, icon, ICONS } from './dom.ts';
import { kickGlass } from './glass.ts';

/** A page of the inspector: its title, what it holds. */
export interface SidePage {
  title: string;
  /** A colour beside the title (a markup's, a test's). */
  dot?: string;
  /** What it holds (`nav`: on to another page, back, closed). */
  build(body: HTMLElement, nav: SideNav): void;
  /** Kept in step with the plan, while it is the page on top. */
  sync?(): void;
  /** What it does as it goes (back from it, or the inspector closed or moved on). */
  leave?(): void;
  /** A way on from a part of what is selected to all of it: a row at its foot. */
  more?: { label: string; onClick(): void };
}
export interface SideSpec extends SidePage {
  /** What it is for: opening it again for the same is a no-op. */
  key: string;
  /** Put away (by Done, Escape, another opened in its place). */
  onClose?(): void;
  /** The id of what it is about (a label's, an item's): said on it (`data-about`), for what checks that it is about what is selected. */
  about?: string;
}
export interface SideNav {
  push(page: SidePage): void;
  pop(): void;
  close(): void;
}

export interface Side {
  el: HTMLElement;
  /** The pill it folds into. */
  bubble: HTMLButtonElement;
  open(spec: SideSpec): void;
  /** A page on, over the one there: it slides in, a back button at its head. */
  push(page: SidePage): void;
  /** Back a page (the one under built afresh, as the plan now is). */
  pop(): void;
  /** How many pages deep it is (1: the first). */
  depth(): number;
  /** The page on top built again, in place (what it is about the same): the height gliding to the new. */
  refill(title?: { title: string; dot?: string }): void;
  /** The page on top kept in step with the plan. */
  sync(): void;
  close(): void;
  /** What it is open for, or null. */
  key(): string | null;
  /** Placed again (the screen, the keyboard or the controls changed). */
  place(): void;
  /** The screen rect it covers, open (for what keeps clear of it) — or, closed, its pill's. */
  rect(): DOMRect | null;
  /** What the pill says: what is selected (its colour), or nothing. */
  setBubble(s: { label: string; dot: string | null }): void;
}

const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
/** How it moves: a spring that settles without a bounce (open, a page on, its height). */
const SPRING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * The inspector on `host` (the editor's screen), kept between `limits().top` and `limits().bottom`
 * (stage px: the controls at the top, the tools at the bottom); `onBubble` opens it from its pill.
 */
export function createSide(host: HTMLElement, limits: () => { top: number; bottom: number }, opts: { signal?: AbortSignal; onBubble?: () => void } = {}): Side {
  const el = h('div', { class: 'glass side', role: 'dialog', 'aria-modal': 'false', 'aria-hidden': 'true', inert: true });
  const dot = h('span', { class: 'bubble-dot', hidden: true });
  const bubble: HTMLButtonElement = h('button', { class: 'glass side-bubble', type: 'button', 'aria-label': 'Settings', 'aria-expanded': 'false', onClick: () => (haptic(), opts.onBubble?.()) }, icon(ICONS.inspector, 24), dot);
  host.append(el, bubble);
  let spec: SideSpec | null = null;
  /** The pages open, the first at the bottom: each its page and what it holds. */
  let stack: { page: SidePage; body: HTMLElement }[] = [];
  const head = h('div', { class: 'side-head' });
  const frame = h('div', { class: 'side-frame' });
  el.append(head, frame);
  /** Where it may stand: under the controls at the top, over those at the foot — and the keyboard, when it is up. */
  const room = () => {
    const hr = host.getBoundingClientRect();
    const vv = window.visualViewport;
    const { top, bottom } = limits();
    const seen = vv ? vv.offsetTop + vv.height - hr.top : hr.height;
    return { top, bottom, low: Math.min(bottom, seen - 8) };
  };
  /** Where it stands for a height: halfway down the room it has; the keyboard up, lifted only as far as it must be. */
  const spot = (hgt: number) => {
    const { top, bottom, low } = room();
    const hh = Math.min(hgt, low - top);
    const middle = top + (bottom - top - hh) / 2;
    return { top: Math.max(top, Math.min(middle, low - hh)), height: hh, max: low - top };
  };
  /** How tall what it holds would have it (its head and its page, as laid out). */
  const natural = () => head.offsetHeight + (stack.at(-1)?.body.scrollHeight ?? 0);
  /** Placed: halfway down at the right, as tall as what it holds within what can be seen. The pill halfway down too. */
  const place = () => {
    const { top, bottom } = room();
    bubble.style.top = `${Math.round(top + (bottom - top) / 2 - bubble.offsetHeight / 2)}px`;
    if (!spec) return;
    const s = spot(natural());
    el.style.top = `${Math.round(s.top)}px`;
    el.style.height = `${Math.round(s.height)}px`;
    el.style.maxHeight = `${Math.round(s.max)}px`;
  };
  /** A change to what it holds, its height and its place gliding from the old to the new (`fn`: the change). */
  const glide = (fn: () => void) => {
    const was = { top: el.offsetTop, height: el.offsetHeight };
    fn();
    place();
    const now = { top: el.offsetTop, height: el.offsetHeight };
    if (!reduced() && (Math.abs(now.height - was.height) > 1 || Math.abs(now.top - was.top) > 1))
      el.animate([{ top: `${was.top}px`, height: `${was.height}px` }, { top: `${now.top}px`, height: `${now.height}px` }], { duration: 420, easing: SPRING });
  };
  /**
   * Its head, for the page on top: a back button (the page under's title) where there is one under, its
   * title, Done. Done and the title are the same elements from page to page — a press on Done begun as
   * the page under it is built again (a field's words taken as the finger left it) still ends on it.
   */
  const done = h('button', { class: 'btn done', type: 'button', onClick: () => (haptic(), close()) }, 'Done');
  const titleEl = h('h2');
  let lead: HTMLElement | null = null;
  let leadFor = '';
  const fillHead = () => {
    const top = stack.at(-1)?.page;
    if (!top) return clear(head);
    const under = stack.at(-2)?.page;
    const want = under ? `back:${under.title}` : top.dot ? `dot:${top.dot}` : '';
    if (want !== leadFor || !head.contains(done)) {
      leadFor = want;
      lead?.remove();
      lead = under
        ? h('button', { class: 'btn back', type: 'button', 'aria-label': `Back to ${under.title}`, onClick: () => (haptic(), pop()) }, icon(ICONS.back, 20), h('span', null, under.title.length > 11 ? 'Back' : under.title))
        : top.dot
          ? h('span', { class: 'side-dot', style: { background: top.dot } })
          : null;
      head.replaceChildren(...(lead ? [lead] : []), titleEl, done);
    }
    titleEl.textContent = top.title;
    el.setAttribute('aria-label', top.title);
  };
  const nav: SideNav = { push: (p) => push(p), pop: () => pop(), close: () => close() };
  /** A page's body, built. */
  const build = (page: SidePage): HTMLElement => {
    const body = h('div', { class: 'side-body' });
    page.build(body, nav);
    if (page.more) {
      const more = page.more;
      body.append(h('button', { class: 'side-more', type: 'button', onClick: () => (haptic(), more.onClick()) }, icon(ICONS.sliders, 18), h('span', null, more.label), icon(ICONS.chevron, 16)));
    }
    return body;
  };
  /** One page's body slid out and another's in, `dir` 1 on (from the right), -1 back. */
  const slide = (from: HTMLElement | null, to: HTMLElement, dir: 1 | -1) => {
    if (from && !reduced()) {
      // (the one going lies over the place it held while it goes: the one coming sets the height)
      const t = from.offsetTop;
      from.style.position = 'absolute';
      from.style.top = `${t}px`;
      from.style.left = '0';
      from.style.right = '0';
      from.inert = true;
      from.animate([{ translate: '0 0', opacity: 1 }, { translate: `${-dir * 28}% 0`, opacity: 0 }], { duration: 300, easing: SPRING }).onfinish = () => from.remove();
      to.animate([{ translate: `${dir * 36}% 0`, opacity: 0 }, { translate: '0 0', opacity: 1 }], { duration: 420, easing: SPRING });
    } else from?.remove();
    frame.append(to);
  };
  const push = (page: SidePage) => {
    if (!spec) return;
    const below = stack.at(-1);
    // (the caret out of the page left: the keyboard goes with it)
    if (el.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    const body = build(page);
    stack.push({ page, body });
    glide(() => {
      fillHead();
      slide(below?.body ?? null, body, 1);
    });
    kickGlass();
  };
  const pop = () => {
    if (!spec || stack.length < 2) return;
    if (el.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    const gone = stack.pop()!;
    gone.page.leave?.();
    const top = stack.at(-1)!;
    // (the page under built afresh: what was chosen on the one left shows)
    top.body = build(top.page);
    glide(() => {
      fillHead();
      slide(gone.body, top.body, -1);
    });
    kickGlass();
  };
  /** The pill's outline, in the pane's own px (for the glass clipped from one to the other). */
  const pillIn = () => {
    const b = { x: bubble.offsetLeft - el.offsetLeft, y: bubble.offsetTop - el.offsetTop, w: bubble.offsetWidth, h: bubble.offsetHeight };
    const W = el.offsetWidth,
      H = el.offsetHeight;
    return `inset(${Math.max(0, b.y)}px ${Math.max(0, W - b.x - b.w)}px ${Math.max(0, H - b.y - b.h)}px ${Math.max(0, b.x)}px round ${b.w / 2}px)`;
  };
  const whole = 'inset(0px 0px 0px 0px round 30px)';
  let morph: Animation | null = null;
  const close = () => {
    if (!spec) return;
    const was = spec;
    spec = null;
    // (the caret out of its fields: the keyboard goes with it)
    if (el.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    for (const p of [...stack].reverse()) p.page.leave?.();
    delete el.dataset.about;
    el.setAttribute('aria-hidden', 'true');
    el.inert = true;
    bubble.setAttribute('aria-expanded', 'false');
    bubble.inert = false;
    // back into its pill: what it holds goes first, then the glass folds into the pill's outline, and the pill is there
    const done = () => {
      el.classList.remove('show');
      bubble.classList.remove('away');
      if (!spec) stack = [];
      kickGlass();
    };
    morph?.cancel();
    morph = null;
    if (reduced()) done();
    else {
      for (const k of [head, frame]) k.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'ease-in' });
      const m = el.animate([{ clipPath: whole }, { clipPath: pillIn() }], { duration: 400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' });
      morph = m;
      // (the pill comes in over the last of it: one shape, never a gap)
      setTimeout(() => !spec && bubble.classList.remove('away'), 300);
      m.onfinish = () => {
        if (morph === m) morph = null;
        m.cancel();
        if (!spec) done();
      };
    }
    was.onClose?.();
  };
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  });
  // (for the screen's life: gone with it)
  const lopts: AddEventListenerOptions = opts.signal ? { signal: opts.signal } : {};
  window.visualViewport?.addEventListener('resize', place, lopts);
  window.visualViewport?.addEventListener('scroll', place, lopts);
  const ro = new ResizeObserver(place);
  ro.observe(host);
  opts.signal?.addEventListener('abort', () => ro.disconnect());
  requestAnimationFrame(place);
  return {
    el,
    bubble,
    open(next) {
      if (spec?.key === next.key) return;
      const was = spec;
      if (was) {
        spec = null;
        for (const p of [...stack].reverse()) p.page.leave?.();
        was.onClose?.();
      }
      spec = next;
      if (next.about) el.dataset.about = next.about;
      else delete el.dataset.about;
      const body = build(next);
      // (shut, or shutting: out of its pill again)
      const fromPill = !el.classList.contains('show') || morph !== null;
      if (fromPill) {
        // out of its pill: laid out where it will stand, clipped to the pill's outline, then grown to its own
        morph?.cancel();
        clear(frame);
        stack = [{ page: next, body }];
        fillHead();
        frame.append(body);
        el.classList.add('show');
        place();
        bubble.classList.add('away');
        if (!reduced()) {
          const m = el.animate([{ clipPath: pillIn() }, { clipPath: whole }], { duration: 560, easing: SPRING });
          morph = m;
          m.onfinish = () => morph === m && (morph = null);
          for (const k of [head, frame]) k.animate([{ opacity: 0 }, { opacity: 0, offset: 0.15 }, { opacity: 1 }], { duration: 440, easing: 'ease-out' });
        } else morph = null;
      } else {
        // open already: what it holds now, the height gliding
        const old = stack.at(-1)?.body ?? null;
        stack = [{ page: next, body }];
        glide(() => {
          fillHead();
          old?.remove();
          clear(frame);
          frame.append(body);
        });
      }
      el.setAttribute('aria-hidden', 'false');
      el.inert = false;
      bubble.setAttribute('aria-expanded', 'true');
      bubble.inert = true;
      kickGlass();
    },
    push,
    pop,
    depth: () => stack.length,
    refill(title) {
      const top = stack.at(-1);
      if (!spec || !top) return;
      if (title && stack.length === 1) Object.assign(top.page, title);
      const scroll = top.body.scrollTop;
      const body = build(top.page);
      glide(() => {
        fillHead();
        top.body.replaceWith(body);
        top.body = body;
      });
      body.scrollTop = scroll;
      kickGlass();
    },
    sync: () => stack.at(-1)?.page.sync?.(),
    close,
    key: () => spec?.key ?? null,
    place,
    rect: () => {
      const hr = host.getBoundingClientRect();
      if (!spec) return bubble.offsetParent ? new DOMRect(hr.left + bubble.offsetLeft, hr.top + bubble.offsetTop, bubble.offsetWidth, bubble.offsetHeight) : null;
      return new DOMRect(hr.left + el.offsetLeft, hr.top + el.offsetTop, el.offsetWidth, el.offsetHeight);
    },
    setBubble({ label, dot: colour }) {
      bubble.setAttribute('aria-label', label);
      dot.hidden = !colour;
      if (colour) dot.style.background = colour;
    },
  };
}

/** A part's name, small, above it (as Settings names a group). */
export function sideLabel(text: string, forId?: string): HTMLElement {
  return h('label', { class: 'side-label', ...(forId ? { for: forId } : {}) }, text);
}
/** A short line of help, under what it is about. */
export function sideHelp(text: string): HTMLElement {
  return h('p', { class: 'side-help' }, text);
}
/** The way a page's form ends: its own buttons in a row (a Clear, say), Done being in its head. */
export function sideActions(...buttons: (HTMLElement | null)[]): HTMLElement {
  return h('div', { class: 'side-actions' }, ...buttons);
}
/** Rows of a kind, together on a card of their own (as Settings groups them). */
export function sideRows(...rows: (HTMLElement | null | false)[]): HTMLElement {
  return h('div', { class: 'side-rows' }, ...rows.filter((r): r is HTMLElement => !!r));
}
/** A row that does something: its icon, what it is, what it is now (`detail`), and where it leads (a chevron) or nothing. */
export function sideRow(o: { icon?: string; label: string; detail?: string; onClick: () => void; chevron?: boolean; danger?: boolean; aria?: string }): HTMLButtonElement {
  return h(
    'button',
    { class: 'side-row' + (o.danger ? ' danger' : ''), type: 'button', 'aria-label': o.aria, onClick: () => (haptic(), o.onClick()) },
    o.icon ? icon(o.icon, 20) : null,
    h('span', { class: 'row-label' }, o.label),
    o.detail ? h('span', { class: 'row-detail' }, o.detail) : null,
    o.chevron ? icon(ICONS.chevron, 14, 'row-chevron') : null,
  );
}
/** A row that is on or off: its icon, what it is, and a switch (the whole row toggles it). */
export function sideSwitch(o: { icon?: string; label: string; on: boolean; onChange: (on: boolean) => void; danger?: boolean }): HTMLButtonElement {
  const b: HTMLButtonElement = h(
    'button',
    { class: 'side-row onoff' + (o.danger ? ' danger' : ''), type: 'button', role: 'switch', 'aria-checked': String(o.on), onClick: () => {
      const on = b.getAttribute('aria-checked') !== 'true';
      b.setAttribute('aria-checked', String(on));
      haptic();
      o.onChange(on);
    } },
    o.icon ? icon(o.icon, 20) : null,
    h('span', { class: 'row-label' }, o.label),
    h('span', { class: 'toggle', 'aria-hidden': 'true' }),
  );
  return b;
}
/** A row with something set right in it: what it is, and the control (a field, a choice) at its end. */
export function sideField(label: string, control: HTMLElement): HTMLElement {
  return h('div', { class: 'side-row setting' }, h('span', { class: 'row-label' }, label), control);
}
/**
 * Choices, every one at once (2026-10-28, Adam: "a way to show all of the suggestions at once … the
 * most wonderful to use as possible on iPad"): rows in groups, a finger's height each, the one it is
 * now ticked; a tap takes one. What is typed (`typed`) shows in bold where it is in each.
 */
export function sideChoices(o: { groups: { label?: string | undefined; items: { value: string; text?: string | undefined; detail?: string | undefined }[] }[]; now?: string | null; typed?: string; onPick: (value: string) => void }): HTMLElement[] {
  const flat = (t: string) => t.replace(/\s+/g, ' ').trim();
  const q = flat(o.typed ?? '').toLowerCase();
  const out: HTMLElement[] = [];
  for (const g of o.groups) {
    if (!g.items.length) continue;
    const rows = g.items.map((it) => {
      const text = flat(it.text ?? it.value);
      const words = h('span', { class: 'row-label' });
      const at = q ? text.toLowerCase().indexOf(q) : -1;
      if (at >= 0) words.append(text.slice(0, at), h('b', null, text.slice(at, at + q.length)), text.slice(at + q.length));
      else words.textContent = text || ' ';
      const on = o.now != null && flat(o.now).toLowerCase() === flat(it.value).toLowerCase();
      return h(
        'button',
        { class: 'side-row option', type: 'button', 'aria-pressed': String(on), 'aria-label': text, onClick: () => (haptic(), o.onPick(it.value)) },
        words,
        it.detail ? h('span', { class: 'row-detail' }, it.detail) : null,
        icon(ICONS.check, 18, 'row-check'),
      );
    });
    out.push(h('div', { class: 'side-section' }, g.label ? sideLabel(g.label) : null, sideRows(...rows)));
  }
  return out;
}
